/**
 * FrameworkPage — /career-transition/framework/:slug
 *
 * One page renders all 6 career-transition frameworks by slug. Each section
 * renders differently based on its kind (prose / checklist / table / script /
 * decision-tree / template / callout). All Access-gated except the free
 * readiness-assessment framework.
 */

import {
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import LockIcon from "@mui/icons-material/Lock";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useNavigate, useParams } from "react-router-dom";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FRAMEWORKS,
  FRAMEWORK_DEEP_CONTENT,
  FrameworkSection,
  getFrameworkBySlug,
} from "../data/careerTransition";
import { L } from "../utils/localized";
import { useAuth } from "../hooks/useAuth";
import { getTierAccess } from "../utils/tier";
import ProUpsellNote from "../components/ProUpsellNote";

// coral via theme.palette.primary — per-product accent deferred (spec D2)

export default function FrameworkPage() {
  const { t, i18n } = useTranslation("pages-products");
  const theme = useTheme();
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const framework = slug ? getFrameworkBySlug(slug, i18n.language) : undefined;
  const content = slug ? L(FRAMEWORK_DEEP_CONTENT, i18n.language)[slug] : undefined;

  if (!framework || !content) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Typography variant="h4">{t("FrameworkPage.notFoundTitle")}</Typography>
        <Button
          onClick={() => navigate("/career-transition")}
          startIcon={<ArrowBackIcon />}
          sx={{ mt: 2 }}
        >
          {t("FrameworkPage.backToCareerTransitionButton")}
        </Button>
      </Container>
    );
  }

  const tierAccess = getTierAccess(user);
  const isAllAccess = tierAccess.canAccessProducts || tierAccess.isModerator;
  const isGated = framework.tier === "all_access" && !isAllAccess;

  // Find next framework for footer nav
  const frameworksResolved = L(FRAMEWORKS, i18n.language);
  const currentIdx = frameworksResolved.findIndex((f) => f.slug === framework.slug);
  const nextFramework =
    currentIdx >= 0 && currentIdx < frameworksResolved.length - 1
      ? frameworksResolved[currentIdx + 1]
      : null;

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh", pb: 10 }}>
      {/* Hero */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
          py: { xs: 5, md: 7 },
        }}
      >
        <Container maxWidth="lg">
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/career-transition")}
            sx={{ color: theme.palette.primary.main, mb: 2 }}
          >
            {t("FrameworkPage.backToCareerTransitionButton")}
          </Button>
          <Stack direction="row" spacing={1} mb={2} flexWrap="wrap" gap={0.5}>
            <Chip
              label={t("FrameworkPage.frameworkNumberChipLabel", { number: framework.number })}
              size="small"
              sx={{ bgcolor: theme.palette.primary.main, color: theme.palette.background.paper, fontWeight: 500, fontSize: "0.7rem" }}
            />
            <Chip
              label={framework.tier === "free" ? t("FrameworkPage.freeChipLabel") : t("FrameworkPage.allAccessChipLabel")}
              icon={framework.tier === "all_access" ? <LockIcon sx={{ fontSize: 12 }} /> : undefined}
              size="small"
              sx={{
                fontWeight: 500,
                fontSize: "0.65rem",
                bgcolor: framework.tier === "free" ? alpha(theme.palette.success.main, 0.12) : alpha(theme.palette.primary.main, 0.12),
                color: framework.tier === "free" ? theme.palette.success.dark : theme.palette.primary.main,
              }}
            />
            <Chip
              label={content.readingTime}
              size="small"
              sx={{ fontSize: "0.7rem", bgcolor: alpha(theme.palette.text.secondary, 0.1), fontWeight: 500 }}
            />
          </Stack>
          <Typography variant="h3" fontWeight={500} sx={{ letterSpacing: "-0.02em", mb: 1.5 }}>
            {framework.title}
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400, maxWidth: 820, lineHeight: 1.5 }}>
            {framework.tagline}
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        {/* Overview */}
        <Paper
          sx={{
            p: 3,
            mb: 4,
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            borderLeft: `4px solid ${theme.palette.primary.light}`,
          }}
        >
          <Typography variant="overline" fontWeight={700} sx={{ color: theme.palette.primary.main, letterSpacing: "0.08em" }}>
            {t("FrameworkPage.overviewLabel")}
          </Typography>
          <Typography variant="body1" sx={{ mt: 1, lineHeight: 1.7 }}>
            {content.overview}
          </Typography>
        </Paper>

        {/* CTA specifically for the readiness-assessment framework */}
        {framework.slug === "readiness-assessment" && (
          <Paper
            sx={{
              p: 3,
              mb: 4,
              borderRadius: 3,
              bgcolor: alpha(theme.palette.primary.main, 0.06),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
              textAlign: "center",
            }}
          >
            <Typography variant="h6" fontWeight={500} mb={1}>
              {t("FrameworkPage.assessmentCtaTitle")}
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              {t("FrameworkPage.assessmentCtaBody")}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AutoAwesomeIcon />}
              onClick={() => navigate("/career-transition/assessment")}
              sx={{
                bgcolor: theme.palette.primary.main,
                fontWeight: 500,
                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.85) },
              }}
            >
              {t("FrameworkPage.startAssessmentButton")}
            </Button>
          </Paper>
        )}

        {/* Gated content preview */}
        {isGated ? (
          tierAccess.isPro ? <ProUpsellNote /> : (
            <Paper
              sx={{
                p: 4,
                borderRadius: 3,
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                textAlign: "center",
              }}
            >
              <LockIcon sx={{ fontSize: 40, color: theme.palette.primary.main, mb: 1 }} />
              <Typography variant="h5" fontWeight={500} mb={1}>
                {t("FrameworkPage.gateTitle")}
              </Typography>
              <Typography variant="body1" color="text.secondary" mb={3} sx={{ maxWidth: 620, mx: "auto" }}>
                {content.sections.length > 3
                  ? t("FrameworkPage.gatePreviewWithMore", {
                      count: content.sections.length,
                      titles: content.sections.slice(0, 3).map((s) => s.title).join(", "),
                      more: content.sections.length - 3,
                    })
                  : t("FrameworkPage.gatePreviewComplete", {
                      count: content.sections.length,
                      titles: content.sections.slice(0, 3).map((s) => s.title).join(", "),
                    })}
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="center">
                <Button
                  variant="contained"
                  onClick={() => navigate("/pricing")}
                  sx={{ bgcolor: theme.palette.primary.main, fontWeight: 500, "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.85) } }}
                >
                  {t("FrameworkPage.unlockAllAccessButton")}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigate("/career-transition/framework/readiness-assessment")}
                  sx={{ borderColor: alpha(theme.palette.primary.main, 0.5), color: theme.palette.primary.main }}
                >
                  {t("FrameworkPage.seeFreeFrameworkButton")}
                </Button>
              </Stack>
            </Paper>
          )
        ) : (
          <Stack spacing={4}>
            {content.sections.map((section, idx) => (
              <SectionRenderer key={idx} section={section} />
            ))}
          </Stack>
        )}

        {/* Footer nav */}
        <Divider sx={{ my: 5 }} />
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="space-between">
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/career-transition")}
            sx={{ borderColor: alpha(theme.palette.primary.main, 0.5), color: theme.palette.primary.main }}
          >
            {t("FrameworkPage.allFrameworksButton")}
          </Button>
          {nextFramework && (
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={() => navigate(`/career-transition/framework/${nextFramework.slug}`)}
              sx={{ bgcolor: theme.palette.primary.main, "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.85) } }}
            >
              {t("FrameworkPage.nextFrameworkButton", { title: nextFramework.title })}
            </Button>
          )}
        </Stack>
      </Container>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Section renderer — switches on kind
// ---------------------------------------------------------------------------

function SectionRenderer({ section }: { section: FrameworkSection }) {
  switch (section.kind) {
    case "prose":
      return <ProseSection section={section} />;
    case "checklist":
      return <ChecklistSection section={section} />;
    case "table":
      return <TableSection section={section} />;
    case "script":
      return <ScriptSection section={section} />;
    case "decision-tree":
      return <DecisionTreeSection section={section} />;
    case "template":
      return <TemplateSection section={section} />;
    case "callout":
      return <CalloutSection section={section} />;
    default:
      return null;
  }
}

function SectionHeader({ section, icon }: { section: FrameworkSection; icon?: React.ReactNode }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
      {icon}
      <Box>
        <Typography variant="h5" fontWeight={500} sx={{ letterSpacing: "-0.01em", lineHeight: 1.3 }}>
          {section.title}
        </Typography>
        {section.subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.3, fontStyle: "italic" }}>
            {section.subtitle}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

function ProseSection({ section }: { section: FrameworkSection }) {
  return (
    <Box>
      <SectionHeader section={section} />
      <Typography variant="body1" sx={{ lineHeight: 1.75 }}>
        {section.body}
      </Typography>
    </Box>
  );
}

function ChecklistSection({ section }: { section: FrameworkSection }) {
  const theme = useTheme();
  return (
    <Box>
      <SectionHeader section={section} />
      <Paper sx={{ p: 2.5, border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`, borderRadius: 2 }}>
        <Stack spacing={1.5}>
          {section.items?.map((item, i) => (
            <Stack key={i} direction="row" spacing={1.5} alignItems="flex-start">
              <Box
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  color: theme.palette.primary.main,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "0.72rem",
                  fontWeight: 500,
                  flexShrink: 0,
                  mt: 0.2,
                }}
              >
                {i + 1}
              </Box>
              <Typography variant="body2" sx={{ lineHeight: 1.65 }}>
                {item}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Paper>
    </Box>
  );
}

function TableSection({ section }: { section: FrameworkSection }) {
  const theme = useTheme();
  return (
    <Box>
      <SectionHeader section={section} />
      <Paper sx={{ overflow: "hidden", border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`, borderRadius: 2 }}>
        {section.rows?.map((row, i) => (
          <Box
            key={i}
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "200px 1fr" },
              gap: 0,
              borderBottom: i < (section.rows?.length ?? 0) - 1 ? `1px solid ${alpha(theme.palette.common.black, 0.08)}` : "none",
            }}
          >
            <Box
              sx={{
                p: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.06),
                fontWeight: 500,
                fontSize: "0.9rem",
                color: theme.palette.primary.main,
                display: "flex",
                alignItems: "flex-start",
              }}
            >
              {row.label}
            </Box>
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" sx={{ lineHeight: 1.65 }}>
                {row.body}
              </Typography>
            </Box>
          </Box>
        ))}
      </Paper>
    </Box>
  );
}

function DecisionTreeSection({ section }: { section: FrameworkSection }) {
  const theme = useTheme();
  return (
    <Box>
      <SectionHeader section={section} icon={<CompareArrowsIcon sx={{ color: theme.palette.primary.main }} />} />
      <Stack spacing={1.5}>
        {section.rows?.map((row, i) => (
          <Paper
            key={i}
            sx={{
              p: 2,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              borderLeft: `4px solid ${theme.palette.primary.light}`,
            }}
          >
            <Typography variant="body2" fontWeight={500} sx={{ color: theme.palette.primary.main, mb: 0.5 }}>
              {row.label}
            </Typography>
            <Typography variant="body2" sx={{ lineHeight: 1.65 }}>
              {row.body}
            </Typography>
          </Paper>
        ))}
      </Stack>
    </Box>
  );
}

function ScriptSection({ section }: { section: FrameworkSection }) {
  const { t } = useTranslation("pages-products");
  const theme = useTheme();
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    if (section.body) {
      navigator.clipboard.writeText(section.body);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  return (
    <Box>
      <SectionHeader section={section} />
      <Paper
        sx={{
          p: 0,
          bgcolor: theme.palette.background.paper,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            px: 2,
            py: 1,
            bgcolor: alpha(theme.palette.background.paper, 0.05),
            borderBottom: `1px solid ${alpha(theme.palette.background.paper, 0.1)}`,
          }}
        >
          <Typography variant="caption" sx={{ color: alpha(theme.palette.background.paper, 0.6), fontFamily: "monospace" }}>
            {t("FrameworkPage.scriptVerbatimLabel")}
          </Typography>
          <Button
            size="small"
            startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
            onClick={handleCopy}
            sx={{ color: alpha(theme.palette.background.paper, 0.8), textTransform: "none", fontSize: "0.75rem" }}
          >
            {copied ? t("FrameworkPage.copiedLabel") : t("FrameworkPage.copyLabel")}
          </Button>
        </Stack>
        <Box
          sx={{
            p: 3,
            color: theme.palette.background.default,
            fontSize: "0.92rem",
            lineHeight: 1.7,
            whiteSpace: "pre-wrap",
            fontFamily: "'Inter', -apple-system, sans-serif",
          }}
        >
          {section.body}
        </Box>
      </Paper>
    </Box>
  );
}

function TemplateSection({ section }: { section: FrameworkSection }) {
  const { t } = useTranslation("pages-products");
  const theme = useTheme();
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    if (section.body) {
      navigator.clipboard.writeText(section.body);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  return (
    <Box>
      <SectionHeader section={section} />
      <Paper
        sx={{
          p: 0,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
          borderRadius: 2,
          overflow: "hidden",
          bgcolor: alpha(theme.palette.primary.main, 0.02),
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            px: 2,
            py: 1,
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
          }}
        >
          <Typography variant="caption" fontWeight={500} sx={{ color: theme.palette.primary.main, letterSpacing: "0.05em" }}>
            {t("FrameworkPage.templateFillInLabel")}
          </Typography>
          <Button
            size="small"
            startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
            onClick={handleCopy}
            sx={{ color: theme.palette.primary.main, textTransform: "none", fontSize: "0.75rem", fontWeight: 500 }}
          >
            {copied ? t("FrameworkPage.copiedLabel") : t("FrameworkPage.copyLabel")}
          </Button>
        </Stack>
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 3,
            fontSize: "0.85rem",
            fontFamily: "'JetBrains Mono', Menlo, monospace",
            whiteSpace: "pre-wrap",
            lineHeight: 1.65,
            color: "text.primary",
          }}
        >
          {section.body}
        </Box>
      </Paper>
    </Box>
  );
}

function CalloutSection({ section }: { section: FrameworkSection }) {
  const { t } = useTranslation("pages-products");
  const theme = useTheme();
  const style = section.calloutStyle ?? "insight";
  const config = {
    insight: { color: theme.palette.primary.main, label: t("FrameworkPage.calloutInsightLabel"), icon: <LightbulbIcon sx={{ color: theme.palette.primary.main }} /> },
    warning: { color: theme.palette.warning.main, label: t("FrameworkPage.calloutWarningLabel"), icon: <WarningAmberIcon sx={{ color: theme.palette.warning.main }} /> },
    counter: { color: theme.palette.error.main, label: t("FrameworkPage.calloutCounterLabel"), icon: <CompareArrowsIcon sx={{ color: theme.palette.error.main }} /> },
  }[style];
  return (
    <Paper
      sx={{
        p: 3,
        bgcolor: alpha(config.color, 0.05),
        border: `1px solid ${alpha(config.color, 0.3)}`,
        borderLeft: `4px solid ${config.color}`,
        borderRadius: 2,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" mb={1}>
        {config.icon}
        <Typography variant="overline" fontWeight={800} sx={{ color: config.color, letterSpacing: "0.08em" }}>
          {config.label} · {section.title}
        </Typography>
      </Stack>
      <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
        {section.body}
      </Typography>
    </Paper>
  );
}
